import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { object, bool, func } from 'prop-types';
import { LazyComponent, StatusToast } from '@fiverr-private/orca';
import { setCheckoutDrawerOpen } from '../../../../actions/checkoutPlan';
import { isCheckoutDrawerOpened } from '../../../../reducers/checkoutPlan';
import { isRecurringGig } from '../../../../reducers/packages';
import { isGigWithActiveWorkProcess } from '../../../../reducers/workProcess';
import { BQ_PAYMENT_DRAWER_IS_SHOWN } from '../../../../utils/bigQueryEvents';
import {
    MP_DRAWER_CLOSE,
    MP_DRAWER_OPEN
} from '../../../../utils/mixPanelEvents';
import { translate } from '../../../../utils/translation';
import { FOOTER_CONFIG } from './config';
import { DRAWER_PAYMENT_TYPES } from './constants';
import OpenDrawerButton from './OpenDrawerButton';

import './index.scss';

const PaymentDrawerFooter = ({
    isGigWithActiveWorkProcess,
    isRecurringGig,
    isCheckoutDrawerOpened,
    setCheckoutDrawerOpen
}, { biEvents, logger }) => {
    const [imported, setImported] = useState(false);
    const [openedDrawer, setDrawerOpened] = useState(false);
    const [loading, setIsLoading] = useState(false);

    // This is preparation for subscription payment in drawer as well
    let paymentOption;
    if (isGigWithActiveWorkProcess) {
        paymentOption = DRAWER_PAYMENT_TYPES.MILESTONE_PAYMENT;
    }
    else if (isRecurringGig) {
        paymentOption = DRAWER_PAYMENT_TYPES.SUBSCRIPTION_PAYMENT;
    }
    const { FooterInfo } = FOOTER_CONFIG[paymentOption];

    const lazyImport = () => import(
        /* webpackChunkName: 'PaymentDrawer' */ './PaymentDrawer'
    );

    const onLazyImportError = (error) => {
        // If need to retry again, toggle the shouldImport to force reload
        setIsLoading(false);
        setImported(false);
        setCheckoutDrawerOpen(false);

        StatusToast.raise({
            message: translate('gig_page_perseus.limited_edition.form.errors.submit'),
            theme: StatusToast.THEMES.ERROR
        });

        logger.error(error, {
            description:'Failed to lazy load payment drawer',
            isGigWithActiveWorkProcess,
            isRecurringGig
        });
    };

    const openDrawer = () => {
        biEvents.sendMixPanelEvent({
            eventName: MP_DRAWER_OPEN
        });

        biEvents.sendBigQueryEvent({
            eventName: BQ_PAYMENT_DRAWER_IS_SHOWN,
            params: {
                paymentOption
            }
        });

        setDrawerOpened(true);
    };

    const closeDrawer = () => {
        setDrawerOpened(false);
        setCheckoutDrawerOpen(false);

        biEvents.sendMixPanelEvent({
            eventName: MP_DRAWER_CLOSE
        });
    };

    const beforeLoad = () => {
        setIsLoading(true);
    };

    const afterLoad = () => {
        setIsLoading(false);
        openDrawer();
    };

    const onClick = () => {
        setCheckoutDrawerOpen(true);
    };

    useEffect(() => {
        if (isCheckoutDrawerOpened) {
            if (!imported) {
                setImported(true);
            } else {
                openDrawer();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCheckoutDrawerOpened]);

    return (
        <footer className="tab-footer drawer-payment-footer">
            <OpenDrawerButton onClick={onClick} loading={loading}/>
            <FooterInfo/>
            <LazyComponent lazyImport={lazyImport}
                shouldImport={imported}
                onError={onLazyImportError}
                beforeLoad={beforeLoad}
                afterLoad={afterLoad}
                componentProps={{ opened: openedDrawer, closeDrawer, paymentOption }}/>
        </footer>
    );
};

PaymentDrawerFooter.propTypes = {
    isGigWithActiveWorkProcess: bool,
    isRecurringGig: bool,
    isCheckoutDrawerOpened: bool,
    setCheckoutDrawerOpen: func
};

PaymentDrawerFooter.contextTypes = {
    biEvents: object,
    logger: object
};

const mapStateToProps = (state) => {
    const { packages } = state;

    return ({
        isGigWithActiveWorkProcess: isGigWithActiveWorkProcess(state),
        isRecurringGig: isRecurringGig(packages),
        isCheckoutDrawerOpened: isCheckoutDrawerOpened(state)
    });
};

const mapDispatchToProps = { setCheckoutDrawerOpen };

export { PaymentDrawerFooter };
export default connect(mapStateToProps, mapDispatchToProps)(PaymentDrawerFooter);
